import { Edit, FileCopyOutlined } from '@mui/icons-material';
import { Avatar, Grid, IconButton, Tooltip } from '@mui/material';
import { stringAvatar } from 'utils/functions';
import { StyledAvatarCustom } from 'component/commonComponents/Avatar/Avatar.style';
import Typography from 'component/commonComponents/Typography';
import RadioGroupComponent from 'component/commonComponents/radioGroup/radioGroupComponent';
import SelectBox from 'component/commonComponents/selectBox/select';
import SwitchComponent from 'component/commonComponents/switch/switchComponent';
import DefaultUptimeTheme from 'component/theme/defaultTheme';
import * as React from 'react';
import { useTheme } from 'styled-components';
import {
  FormControlContainer,
  HeaderName,
  Information,
  InformationDetail,
  StyledDelegationContainer,
  StyledDelegationDetails,
  StyledEditProfile,
} from './editProfile.style';

const INITIAL_LANGUAGE = {
  code: "",
  name: ""
}

const INITIAL_TIME_FORMAT = {
  value: "",
  name: ""
}

const LANGUAGE_PROPERTY = 'language';
const TIME_FORMAT_PROPERTY = 'time_format';
const MEASUREMENT_SYSTEM_PROPERTY = 'measurement_system';

interface LanguageItem {
  name: string;
  code: string;
}

interface TimeFormatItem {
  name: string;
  value: string;
}

interface Country {
  iso_alpha3: string;
  is_limited: boolean;
  name: string;
}

export interface IEditProfileProps {
  userInfo: any,
  preferenceSetting: {
    id: string,
    user_uid: string,
    preferences: {
      language: string,
      measurement_system: string,
      time_format: string
    }
  },
  onChange: <T,> (key: string, value: T) => void,
  locale: {
    id: string;
    employeeID: string;
    email: string;
    location: string;
    reportTo: string;
    preferences: string;
    language: string;
    timeFormat: string;
    measurementSystem: string;
    metric: string;
    imperial: string;
    hours12: string;
    hours24: string;
    country: string;
    delegation: string;
    edit: string;
    enable: string;
    delegationDetails: string;
    delegatee: string;
    start: string;
    end: string;
    delegationDialog: {
        title: string,
        txtCancel: string,
        txtSubmit: string,
        txtSearch: string,
        maxNumberUserText: string,
        startDate: string,
        endDate: string,
        confirmText: string,
        delegatedUser: string,
        remove: string,
        checkEmail: {
           placeholder: string,
           searchLabel: string,
           error_link : string,
           error_2 : string,
           errorInvalid : string,
           errorUser : string,
        }
    },
    selectBox: {
      open: string,
      close: string,
    }
  },
  languageOptions: LanguageItem[],
  enabledMeasurementSystem: boolean,
  enabledLanguage: boolean,
  enabledDelegation: boolean,
  delegationApproval: {
    delegate: [{
      uid: string,
      name: string,
      user_name: string,
      email: string,
  }],
  delegation_uid: string,
  id: string,
  user: {
    name: string,
    email: string,
    uid: string,
  },
    approvals: boolean,
    start_date: string,
    end_date: string,
  },
  startDate: string,
  endDate: string,
  isEditDelegation: () => void,
  isShowEditBtn: boolean,
  isDelegateInfoSubmited: boolean,
  isMarketplaceEnabled: boolean;
  countryList: Country[];
  marketplaceUserCountry: Country;
  onCountryChange: (country: Country) => void;
  queryUserByUserName?: boolean;
}

/**
 * Container for Edit profle form.
 */
const EditProfile: React.FC<IEditProfileProps> = (props) => {
  const { languageOptions, enabledMeasurementSystem, enabledLanguage, enabledDelegation, isShowEditBtn, isDelegateInfoSubmited, isMarketplaceEnabled = false, locale, countryList, marketplaceUserCountry, onCountryChange, queryUserByUserName } = props;
  const theme = useTheme() as typeof DefaultUptimeTheme;
  const [ delegationEnable, setDelegationEnable ] = React.useState(props.delegationApproval.approvals);

  const timeFormatOptions = [{
    value: "12",
    name: locale?.hours12
  }, {
    value: "24",
    name: locale?.hours24
  }];

  const [formReference, setFormReference] = React.useState({
    language: INITIAL_LANGUAGE,
    time_format: INITIAL_TIME_FORMAT,
    measurement_system: ""
  });

  const hasOneLanguage = React.useMemo(() => {
    return languageOptions?.length === 1;
  }, [languageOptions])

  React.useEffect(() => {
    const languageSelected: LanguageItem | undefined = hasOneLanguage
      ? languageOptions?.[0]
      : languageOptions?.find((i: LanguageItem) => {
        return i.code === props.preferenceSetting?.preferences?.language;
      });
    const timeFormatFound = timeFormatOptions.find(
      (i: TimeFormatItem) =>
        i.value === props.preferenceSetting.preferences.time_format
    );
    setFormReference({
      language: languageSelected || INITIAL_LANGUAGE,
      measurement_system:
        props.preferenceSetting.preferences.measurement_system,
      time_format: timeFormatFound || INITIAL_TIME_FORMAT,
    });
    setDelegationEnable(props.delegationApproval.approvals)
  }, [props]);

  const valueMeasurementChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setFormReference({
      ...formReference,
      [MEASUREMENT_SYSTEM_PROPERTY]: e.target.value
    });
    onFormChange(MEASUREMENT_SYSTEM_PROPERTY, e.target.value);
  }

  const valueLanguageChange = (value: LanguageItem): void => {
    setFormReference({
      ...formReference,
      [LANGUAGE_PROPERTY]: value
    });
    onFormChange<LanguageItem>(LANGUAGE_PROPERTY, value);
  }

  const valueTimeFormatChange = (value: TimeFormatItem): void => {
    setFormReference({
      ...formReference,
      [TIME_FORMAT_PROPERTY]: value
    });
    onFormChange<TimeFormatItem>(TIME_FORMAT_PROPERTY, value);
  }

  const valueEnableChange = (): void => {
    // onFormChange<Boolean>('enable', !delegationEnable)
    onFormChange<Boolean>('enable', !props.delegationApproval?.approvals)

    // setDelegationEnable(!delegationEnable)
  }

  const onFormChange = <T,>(key: string, value: T) => {
    props.onChange(key, value);
  }

  const onClickCopy = () => {
    const copyText = props.userInfo?.manager_email.toString();
    navigator.clipboard.writeText(copyText);
  }

  return (
    <StyledEditProfile>
        <Information>
          <Grid container direction='row'>
            <Grid item xs={12} sm={12} md={2}>
              <HeaderName>{props.userInfo?.name}</HeaderName>
              <Grid container justifyContent='center'>
                {!!props.userInfo?.photo ?
                  <Avatar
                    sx={{width: 121, height: 121}}
                    src={props.userInfo?.photo}
                    alt="user avatar"
                    slotProps={{
                      img: {
                        title: props.userInfo?.name,
                      }
                    }}
                  />
                  :
                  <StyledAvatarCustom>
                    <Avatar 
                      {...stringAvatar(props.userInfo?.avatar_name, 121)}
                      className="avatar-large"
                    />
                  </StyledAvatarCustom>
                }
              </Grid>
            </Grid>
            <InformationDetail item xs={12} sm={12} md={10} lg={8} container>
              <Grid item xs={4} sm={3} md={2} className='label'>
                {locale?.employeeID}
              </Grid>
              <Grid item xs={8} sm={9} className='content' id='lbl-edit_profile-employee_number'>
                {props.userInfo?.employee_number}
              </Grid>
              <Grid item xs={4} sm={3} md={2} className='label'>
                {locale?.email}
              </Grid>
              <Grid item xs={8} sm={9} className='content' id='lbl-edit_profile-email'>
                {props.userInfo?.email}
              </Grid>
              <Grid item xs={4} sm={3} md={2} className='label'>
                {locale?.location}
              </Grid>
              <Grid item xs={8} sm={9} className='content' id='lbl-edit_profile-location'>
                {props.userInfo?.location}
              </Grid>
              <Grid item xs={4} sm={3} md={2} className='label manager'>
                {locale?.reportTo}
              </Grid>
              <Grid item xs={8} sm={9} className='content manager' id='lbl-edit_profile-manager_name'>
                {props.userInfo?.manager_name}
                {(!!props.userInfo?.manager_name && !!props.userInfo?.manager_email) &&
                  <Tooltip title={props.userInfo?.manager_email} arrow placement="right">
                    <IconButton className='copy-button' aria-label="copy" onClick={onClickCopy}>
                      <FileCopyOutlined className='copy-icon'/>
                    </IconButton>
                  </Tooltip>
                }
              </Grid>
              <FormControlContainer container>
                <Grid item xs={12} className='header'>
                  {locale?.preferences}
                </Grid>

                <Grid container>
                  {enabledLanguage && <Grid item xs={6}>

                    <Grid item xs={12} className='content'>
                      <SelectBox
                        id='language'
                        style={{}}
                        placeholder=''
                        boldLabel={true}
                        error={false}
                        required={false}
                        label={locale?.language}
                        options={languageOptions}
                        value={formReference.language}
                        disableClearable={true}
                        handleChange={(_: any, value: LanguageItem) =>
                          valueLanguageChange(value)
                        }
                        disabled={hasOneLanguage}
                        valueKey='code'
                        locale={locale}
                        sx={{
                          "& label":{
                            lineHeight:'45px',marginBottom:"0 !important"
                          }
                        }}
                      ></SelectBox>
                    </Grid>
                  </Grid>}

                  <Grid item xs={6}>

                    <Grid item xs={12} className='content time-format'>
                      <SelectBox
                        id={'time_format'}
                        style={{}}
                        placeholder=''
                        boldLabel={true}
                        error={false}
                        required={false}
                        label={locale?.timeFormat}
                        options={timeFormatOptions}
                        value={formReference.time_format}
                        disableClearable={true}
                        handleChange={(_: any, value: TimeFormatItem) =>
                          valueTimeFormatChange(value)
                        }
                        locale={locale}
                        sx={{
                          "& label":{
                            lineHeight:'45px',marginBottom:"0 !important"
                          }
                        }}
                      ></SelectBox>
                    </Grid>
                  </Grid>
                </Grid>

                {enabledMeasurementSystem &&
                  <Grid container>
                    <Grid item xs={6}>
                    <Grid item xs={12} className='label measurement-padding'>
                      {locale?.measurementSystem}
                    </Grid>
                    <Grid item xs={6} className='content'></Grid>
                    <Grid item xs={12} className='content measurement'>
                      <RadioGroupComponent
                        selected={formReference.measurement_system}
                        name={'measurement_system'}
                        data={[
                          {
                            value: 'metric',
                            name: locale?.metric,
                          },
                          {
                            value: 'imperial',
                            name: locale?.imperial,
                          },
                        ]}
                        isRow={true}
                        onChange={(e: any) => valueMeasurementChange(e)}
                        id='grp-edit_profile-measurement_system'
                      ></RadioGroupComponent>
                    </Grid>
                    </Grid>
                    {isMarketplaceEnabled && (
                      <Grid container item xs={6} pt={`${theme.uptimeSpace[10]}px`}>
                        <Grid item xs={12} pt='5px' className='content'>
                          <SelectBox
                            placeholder=''
                            id='country_residence'
                            boldLabel={true}
                            error={false}
                            required={false}
                            label={locale?.country}
                            options={countryList}
                            value={marketplaceUserCountry || {}}
                            valueKey='iso_alpha3'
                            disableClearable
                            handleChange={(_: any, value: Country) => onCountryChange(value)}
                            sx={{
                              "& label":{
                                lineHeight:'45px',marginBottom:"0 !important"
                              }
                            }}
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Grid>
                }

              </FormControlContainer>

              {
                enabledDelegation &&
                <StyledDelegationContainer container>
                <Grid item xs={3} lg={2} md={2} sm={2} className="header">
                  {locale?.delegation}
                </Grid>
                <Grid item xs={1} lg={1} md={1} sm={1} className="enable-wrapper">
                  <SwitchComponent className="enable-checkbox"
                      id='enable'
                      checked={props.delegationApproval?.approvals}
                      onChange={() => {
                        valueEnableChange()
                      }}
                  />
                </Grid>
                {
                  isShowEditBtn && delegationEnable && isDelegateInfoSubmited && (
                    <Grid item xs={4} className="edit-wrapper">
                    <Typography className="edit-button" kind="default" as="p" aria-label="edit" onClick={props.isEditDelegation} disabled={true}>
                      <Edit className="edit-button-icon" id="btn-editprofile-edit"/>
                      {locale?.edit}
                      </Typography>
                    </Grid>
                  )
                }
            </StyledDelegationContainer>
              }
                {
                  enabledDelegation && isDelegateInfoSubmited && props.delegationApproval?.delegate[0]?.uid && props.delegationApproval?.approvals && (
                    <StyledDelegationDetails xs={12} container>
                      <Grid item xs={12} className="detail-title">
                        {locale?.delegationDetails}
                      </Grid>
                      <Grid item xs={12} className="delegation-date" container>
                        <Grid item xs={2} className="label">
                          {locale?.start}:
                        </Grid>
                        <Grid item xs={10} className="content" id="lbl-edit_profile-delegation_start">
                          {props.startDate}
                        </Grid>
                        <Grid item xs={2} className="label">
                          {locale?.end}:
                        </Grid>
                        <Grid item xs={10} className="content" id="lbl-edit_profile-delegation_end">
                          {props.endDate}
                        </Grid>
                      </Grid>
                      {
                        props.delegationApproval.delegate.map(item => (
                          <>
                            <Grid id={item.uid} xs={12} container>
                              <Grid item xs={2} className="label">
                                  {locale?.delegatee}:
                              </Grid>
                              <Grid item xs={10} className="content" id="lbl-edit_profile-delegatee">
                                  {queryUserByUserName? item.user_name : item.email}
                              </Grid>
                            </Grid>
                          </>
                        ))
                      }
                    </StyledDelegationDetails>
                  )
                }
            </InformationDetail>
          </Grid>
        </Information>
    </StyledEditProfile>
  );
}

export default React.memo(EditProfile);
